import React, { useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { getUrl, uploadData } from 'aws-amplify/storage';
// QUIRÚRGICO: Añadida mutación de notificación
import { updateDocumentRequest, deleteDocumentRequest, createNotification } from '../graphql/mutations'; 
import { FaFileDownload, FaMoneyBillWave, FaImage, FaCloudUploadAlt, FaSpinner, FaTrashAlt } from 'react-icons/fa';
import './DocumentRequestsManager.css';

const DocumentRequestsManager = ({ requests, onUpdate }) => {
    const client = generateClient();
    const [uploadingId, setUploadingId] = useState(null);

    // --- FUNCIÓN PARA ELIMINAR (SE MANTIENE IGUAL) ---
    const handleDelete = async (id) => {
        if (!window.confirm("¿Está seguro de eliminar este trámite? Esta acción no se puede deshacer.")) return;
        
        try {
            await client.graphql({
                query: deleteDocumentRequest,
                variables: { input: { id } },
                authMode: 'userPool',
            });
            alert("✅ Registro eliminado correctamente.");
            onUpdate(); 
        } catch (e) {
            console.error("Error al eliminar:", e);
            alert("No se pudo eliminar el registro.");
        }
    };

    // --- FUNCIÓN DE DESCARGA CORREGIDA ---
    const handleDownload = async (key, type) => {
        try {
            if (!key) {
                console.log("Key recibida:", key);
                return alert(`No hay ${type} disponible para este registro.`);
            }
            
            // CORRECCIÓN: Aseguramos que el path sea exacto como lo espera Storage
            // Si la key ya trae 'public/', la usamos tal cual, si no, se lo ponemos.
            const cleanPath = key.startsWith('public/') ? key : `public/${key}`;
            
            const signedUrl = await getUrl({ 
                path: cleanPath, 
                options: { 
                    expiresIn: 900,
                    validateObjectExistence: true // Verifica que el archivo exista antes de abrir
                } 
            });

            window.open(signedUrl.url.toString(), '_blank');
        } catch (error) { 
            console.error("Error en descarga:", error);
            alert("Error al obtener el archivo. Verifique que el archivo exista en el servidor."); 
        }
    };

    const handleSetPrice = async (req) => {
        const price = prompt("Ingrese el presupuesto (USD) para este trámite:");
        if (!price || isNaN(price)) return;
        try {
            await client.graphql({
                query: updateDocumentRequest,
                variables: { input: { id: req.id, priceUSD: parseFloat(price), status: "PENDIENTE_PAGO" } },
                authMode: 'userPool',
            });

            // INYECCIÓN QUIRÚRGICA: Notificar al cliente que tiene presupuesto asignado
            await client.graphql({
                query: createNotification,
                variables: {
                    input: {
                        recipientEmail: req.customerEmail,
                        title: "💰 PRESUPUESTO ASIGNADO",
                        message: `Se ha asignado un costo de $${price} a su trámite de ${req.areaLegal}. Por favor, proceda al pago en su panel.`,
                        isRead: false
                    }
                },
                authMode: 'userPool'
            });

            onUpdate();
        } catch (e) { alert("Error al asignar precio."); }
    };

    const handleUploadFinalFile = async (req, file) => {
        if (!file) return;
        setUploadingId(req.id);
        try {
            // Limpieza de nombre para el archivo final
            const cleanFinalName = file.name.replace(/[^a-zA-Z0-9.]/g, "_");
            const fileName = `final_docs/final-${req.id}-${Date.now()}_${cleanFinalName}`;
            
            await uploadData({
                path: `public/${fileName}`,
                data: file,
                options: { contentType: file.type }
            }).result;

            await client.graphql({
                query: updateDocumentRequest,
                variables: { input: { id: req.id, status: "COMPLETADO", resultUrl: fileName } },
                authMode: 'userPool',
            });

            // INYECCIÓN QUIRÚRGICA: Notificar al cliente que su documento está listo
            await client.graphql({
                query: createNotification,
                variables: {
                    input: {
                        recipientEmail: req.customerEmail,
                        title: "✅ TRÁMITE COMPLETADO",
                        message: `Su documento de ${req.areaLegal} ha sido procesado con éxito. Ya puede descargarlo desde su panel de usuario.`,
                        isRead: false
                    }
                },
                authMode: 'userPool'
            });

            alert("✅ Documento final enviado al cliente.");
            onUpdate();
        } catch (e) {
            console.error("Error al subir final:", e);
            alert("Error al subir archivo final.");
        } finally {
            setUploadingId(null);
        }
    };

    return (
        <div className="admin-container-pro">
            <div className="admin-header-main">
                <div className="header-text">
                    <h3>📂 Gestión de Trámites Legales</h3>
                    <p>Dr. Giovanni Scarvaci & Asociados</p>
                </div>
                <span className="badge-count">{requests.length} Solicitudes</span>
            </div>
            
            <div className="table-wrapper">
                <table className="legal-table">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Cliente / Contacto</th>
                            <th>Descripción</th>
                            <th>Documento</th>
                            <th>Estado / Pago</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {requests.map((req) => (
                            <tr key={req.id} className={`row-status-${req.status}`}>
                                <td>{new Date(req.requestDate).toLocaleDateString()}</td>
                                <td>
                                    <strong>{req.customerEmail}</strong><br/>
                                    <small>{req.phone || req.customerPhone}</small>
                                </td>
                                <td><p style={{fontSize: '11px', maxWidth: '200px'}}>{req.description}</p></td>
                                <td>
                                    {/* CORRECCIÓN CLAVE: Usamos req.clientFileKey que es el nombre real en tu base de datos */}
                                    <button 
                                        className="btn-action-table" 
                                        onClick={() => handleDownload(req.clientFileKey, 'documento')}
                                    >
                                        <FaFileDownload /> Ver Base
                                    </button>
                                </td>
                                <td>
                                    <span className={`status-pill ${req.status}`}>{req.status}</span>
                                    {req.receiptKey && (
                                        <button className="btn-mini-receipt" onClick={() => handleDownload(req.receiptKey, 'comprobante')}>
                                            <FaImage /> Pago
                                        </button>
                                    )}
                                </td>
                                <td>
                                    <div style={{display: 'flex', gap: '5px'}}>
                                        {/* Pasamos 'req' completo para tener el email del cliente en la notificación */}
                                        <button className="btn-price" onClick={() => handleSetPrice(req)} title="Asignar Precio">
                                            <FaMoneyBillWave />
                                        </button>
                                        
                                        <label className="btn-upload-final" title="Subir Final">
                                            {uploadingId === req.id ? <FaSpinner className="spin" /> : <FaCloudUploadAlt />}
                                            <input type="file" hidden onChange={(e) => handleUploadFinalFile(req, e.target.files[0])} />
                                        </label>

                                        <button className="btn-delete-legal" onClick={() => handleDelete(req.id)} title="Eliminar">
                                            <FaTrashAlt color="#ff4d4d" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DocumentRequestsManager;